import { RefreshRounded } from "@mui/icons-material";
import {
    Alert,
    AlertTitle,
    Box,
    Container,
    Grid,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxStatusCard from "components/UI/Cards/AcxStatusCard";
import IAmdTimeline from "models/IAmdTimeline";
import ISagaState from "models/ISagaState";
import { observer } from "mobx-react";
import React from "react";
import { ServiceError } from "services/BaseService";
import { PipelineService } from "services/PipelineService";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { extractGuid } from "utils/StringUtils";
import { useStore } from "utils/useStore";
import AcxLoadingIndicator from "../AcxLoadingIndicator";
import AmdTimelineHorizontalBarGraph from "./AmdTimelineHorizontalBarGraph";
import AmdTimeline from "./AmdTimeline";
import {
    buildPipelineSummary, formatServiceLevel,
    getTimelineData,
    ProcessingStatus,
    timelineEventsEqual
} from "../../../utils/AmdTimelineData";

interface TimelineLoadError {
    title: string;
    message: string;
}

interface StatusSummaryCard {
    label: string;
    value: string;
    tooltipText: string;
    valueVariant?: "h1" | "h6";
    progress?: number;
    terminalStateColor?: string;
}

function formatDate(value: string) {
    return new Date(value).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

function formatCheckedAt(value: string) {
    return new Date(value).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
    });
}

function formatProcessingStatus(status: string) {
    return status.replace(/([a-z])([A-Z])/g, "$1 $2");
}

export const AmdTimelineWithGraph = observer(function AmdTimelineWithGraph({
    amdId,
}: {
    amdId: string;
}) {
    const authStore = useStore(AuthStore);
    const pipelineService = React.useMemo(() => new PipelineService(), []);

    const [timeline, setTimeline] = React.useState<IAmdTimeline | undefined>();
    const [sagas, setSagas] = React.useState<ISagaState[] | undefined>();
    const [now, setNow] = React.useState(Date.now());
    const [isLoading, setIsLoading] = React.useState(false);
    const [loadError, setLoadError] = React.useState<
        TimelineLoadError | undefined
    >();
    const requestControllerRef = React.useRef<AbortController>();
    const requestNumberRef = React.useRef(0);

    const loadTimeline = React.useCallback(
        async (
            orgId: string,
            normalizedAmdId: string,
            clearExisting: boolean,
        ) => {
            requestControllerRef.current?.abort();
            const controller = new AbortController();
            const requestNumber = ++requestNumberRef.current;
            requestControllerRef.current = controller;

            if (clearExisting) {
                setTimeline(undefined);
                setSagas(undefined);
            }
            setLoadError(undefined);
            setIsLoading(true);

            try {
                const [result, sagaResult] = await Promise.all([
                    pipelineService.getTimelineByAMD(
                        orgId,
                        normalizedAmdId,
                        controller.signal,
                    ),
                    pipelineService
                        .getAllSagasStateByAMD(
                            orgId,
                            normalizedAmdId,
                            controller.signal,
                        )
                        .catch(() => undefined),
                ]);
                if (requestNumber === requestNumberRef.current) {
                    setTimeline((current) =>
                        current?.amdID === result.amdID &&
                        timelineEventsEqual(
                            current.timelineEvents,
                            result.timelineEvents,
                        )
                            ? {
                                  ...result,
                                  timelineEvents: current.timelineEvents,
                              }
                            : result,
                    );
                    setSagas(sagaResult);
                    setNow(Date.now());
                }
            } catch (error) {
                if (
                    controller.signal.aborted ||
                    requestNumber !== requestNumberRef.current
                ) {
                    return;
                }

                setLoadError(
                    error instanceof ServiceError &&
                        error.response.status === 404
                        ? {
                              title: "AMD not found",
                              message:
                                  "The requested AMD could not be found in this organization.",
                          }
                        : {
                              title: "Unable to load telemetry",
                              message:
                                  "We couldn't load telemetry for this AMD.",
                          },
                );
            } finally {
                if (requestNumber === requestNumberRef.current) {
                    setIsLoading(false);
                }
            }
        },
        [pipelineService],
    );

    const normalizedAmdId = extractGuid(amdId);
    const orgId = authStore.orgStore.selectedOrganization?.id;

    React.useEffect(() => {
        if (!normalizedAmdId || !orgId) {
            requestControllerRef.current?.abort();
            requestNumberRef.current += 1;
            setTimeline(undefined);
            setSagas(undefined);
            setLoadError(undefined);
            setIsLoading(false);
            return;
        }

        void loadTimeline(orgId, normalizedAmdId, true);

        return () => requestControllerRef.current?.abort();
    }, [loadTimeline, normalizedAmdId, orgId]);

    React.useEffect(() => {
        if (
            !timeline ||
            timeline.processingStatus.toLowerCase() !== ProcessingStatus
        ) {
            return;
        }

        const timer = window.setInterval(() => setNow(Date.now()), 30_000);
        return () => window.clearInterval(timer);
    }, [timeline]);

    const refreshTimeline = React.useCallback(() => {
        if (!normalizedAmdId || !orgId) return;
        void loadTimeline(orgId, normalizedAmdId, false);
    }, [loadTimeline, normalizedAmdId, orgId]);

    const displayLoading = !timeline && isLoading;
    const hasEvents = (timeline?.timelineEvents.length ?? 0) > 0;
    const outsideLookupWindow =
        timeline?.telemetryStatus === "outsideLookupWindow";
    const noTelemetryInWindow =
        timeline?.telemetryStatus === "noTelemetryInWindow" ||
        (timeline?.telemetryStatus === "found" && !hasEvents);
    const timelineEvents = timeline?.timelineEvents;
    const timelineData = React.useMemo(
        () => getTimelineData(timelineEvents ?? []),
        [timelineEvents],
    );
    const pipelineSummary = React.useMemo(
        () =>
            timeline
                ? buildPipelineSummary(timeline, timelineData, sagas, now)
                : undefined,
        [now, sagas, timeline, timelineData],
    );
    const statusSummaryCards: StatusSummaryCard[] = timeline
        ? [
              {
                  label: "Pipeline Status",
                  value: formatProcessingStatus(timeline.processingStatus),
                  tooltipText: "Current processing state for this AMD.",
                  valueVariant: "h1",
              },
              {
                  label: "Service Level",
                  value: formatServiceLevel(timeline.serviceLevel),
                  tooltipText:
                      "Quality-of-service processing lane used for this AMD.",
                  valueVariant: "h1",
              },
              {
                  label: "Time Processing",
                  value: pipelineSummary?.processingDuration ?? "Not available",
                  tooltipText:
                      !hasEvents && outsideLookupWindow
                          ? "Telemetry is no longer available, so processing time cannot be calculated."
                          : pipelineSummary
                          ? "Time when at least one microservice was actively processing this AMD."
                          : "Waiting for the first telemetry event.",
                  valueVariant: "h1",
              },
              {
                  label: "Time Queueing",
                  value: pipelineSummary?.queueingDuration ?? "Not available",
                  tooltipText:
                      !hasEvents && outsideLookupWindow
                          ? "Telemetry is no longer available, so queueing time cannot be calculated."
                          : pipelineSummary
                          ? timeline.processingStatus.toLowerCase() !== ProcessingStatus
                              ? "Time between active processing intervals, from first through last telemetry."
                              : "Time outside active processing intervals, from first telemetry through the current time."
                          : "Waiting for the first telemetry event.",
                  valueVariant: "h1",
              },
              {
                  label: "Estimated time to RTS",
                  value: pipelineSummary?.rtsEstimate ?? "Not available",
                  tooltipText:
                      pipelineSummary?.rtsDetail ??
                      "Waiting for this AMD's telemetry and RTS requirements.",
                  progress: pipelineSummary?.rtsProgress,
                  terminalStateColor: pipelineSummary?.rtsTerminalStateColor,
              },
          ]
        : [];

    return (
        <Container maxWidth="xl">
            {timeline && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    spacing={2}
                    sx={{ my: 2 }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Last checked at {formatCheckedAt(timeline.queriedAtUtc)}
                    </Typography>
                    {!outsideLookupWindow && (
                        <AcxButton
                            color="white"
                            startIcon={<RefreshRounded />}
                            loading={isLoading}
                            disabledTooltip="Refreshing timeline"
                            onClick={refreshTimeline}
                        >
                            {isLoading ? "Refreshing" : "Refresh"}
                        </AcxButton>
                    )}
                </Stack>
            )}

            {loadError && (
                <Alert
                    severity="error"
                    sx={{ my: 2, borderRadius: 2 }}
                    action={
                        <AcxButton
                            color="white"
                            loading={isLoading}
                            onClick={refreshTimeline}
                        >
                            Try again
                        </AcxButton>
                    }
                >
                    <AlertTitle>{loadError.title}</AlertTitle>
                    {loadError.message}
                    {timeline && " The existing timeline may be out of date."}
                </Alert>
            )}

            {displayLoading && (
                <AcxLoadingIndicator size={64} alternate="PuffLoader" />
            )}

            {timeline && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            md: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(5, minmax(0, 1fr))",
                        },
                        gap: 2,
                        mb: 3,
                    }}
                >
                    {statusSummaryCards.map(
                        ({
                            label,
                            value,
                            tooltipText,
                            valueVariant = "h6",
                            progress,
                            terminalStateColor,
                        }) => (
                            <AcxStatusCard
                                key={label}
                                caption={label}
                                captionVariant="caption"
                                tooltipText={tooltipText}
                                containerStyles={{
                                    minWidth: 0,
                                    border: `1px solid ${theme.palette.gray[200]}`,
                                    borderRadius: theme.shape.borderRadius * 2,
                                    boxShadow: "none",
                                }}
                                primaryContent={
                                    <Stack sx={{ width: "100%" }}>
                                        <Typography
                                            variant={valueVariant}
                                            sx={{
                                                mt: 0.25,
                                                color:
                                                    valueVariant === "h1"
                                                        ? "text.secondary"
                                                        : undefined,
                                                fontFamily:
                                                    valueVariant === "h1"
                                                        ? theme.typography
                                                              .fontFamily
                                                        : undefined,
                                                fontWeight:
                                                    valueVariant === "h1"
                                                        ? 500
                                                        : undefined,
                                            }}
                                        >
                                            {value}
                                        </Typography>
                                        {progress !== undefined ? (
                                            <LinearProgress
                                                variant="determinate"
                                                value={progress}
                                                sx={{
                                                    mt: 1,
                                                    mb: 0,
                                                    height: 5,
                                                    borderRadius: 2,
                                                }}
                                            />
                                        ) : terminalStateColor ? (
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    mt: 1,
                                                    height: 5,
                                                    borderRadius: 2,
                                                    backgroundColor:
                                                        terminalStateColor,
                                                }}
                                            />
                                        ) : null}
                                    </Stack>
                                }
                            />
                        ),
                    )}
                </Box>
            )}

            {timeline && (timeline.processingIssues?.length ?? 0) > 0 && (
                <Alert
                    severity={
                        timeline.processingIssues.some(
                            (issue) => issue.kind === "failure",
                        )
                            ? "error"
                            : "warning"
                    }
                    sx={{ mb: 3, borderRadius: 2 }}
                >
                    <AlertTitle>Processing diagnostics</AlertTitle>
                    <Stack spacing={1.5} component="ul" sx={{ pl: 2.5, mb: 0 }}>
                        {timeline.processingIssues.map((issue, index) => (
                            <Box
                                component="li"
                                key={`${issue.kind}-${
                                    issue.occurredOn ?? index
                                }`}
                                sx={{ pl: 0.5 }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {issue.message}
                                </Typography>
                                {(issue.source ||
                                    issue.exceptionType ||
                                    issue.occurredOn) && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {[
                                            issue.source,
                                            issue.exceptionType,
                                            issue.occurredOn
                                                ? new Date(
                                                      issue.occurredOn,
                                                  ).toLocaleString()
                                                : undefined,
                                        ]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Alert>
            )}

            {timeline && outsideLookupWindow && (
                <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
                    <AlertTitle>Telemetry unavailable for this AMD</AlertTitle>
                    This file falls outside the current telemetry lookup window
                    ({formatDate(timeline.lookupWindowStartUtc)}–
                    {formatDate(timeline.lookupWindowEndUtc)}). Historical
                    telemetry is not retained and cannot be retrieved.
                </Alert>
            )}

            {timeline && noTelemetryInWindow && (
                <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                    <AlertTitle>No telemetry has been recorded</AlertTitle>
                    This AMD is within the current lookup window. If the file is
                    still processing, wait a moment and refresh.
                </Alert>
            )}

            {timeline && hasEvents && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            lg: "repeat(3, minmax(0, 1fr))",
                        },
                        alignItems: "start",
                        gap: 2,
                    }}
                >
                    <AmdTimelineHorizontalBarGraph
                        data={timelineData.chartData}
                    />
                    <Grid sx={{ gridColumn: { lg: "span 2" } }}>
                        <AmdTimeline timeline={timeline} />
                    </Grid>
                </Box>
            )}
        </Container>
    );
});
